import React, { useMemo, useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import Round from "../badge/Round";

export interface ChartLineDataItem {
  day: string;
  dayName: string;
  value: number;
}

export interface DashboardChartLineProps {
  data: ChartLineDataItem[];
  topDate?: string | null; // 하위 호환성 유지, 내부에서 사용 안 함
  className?: string;
}

// 시간값(24시간)을 "X요일 오전/오후 XX시" 형식으로 변환
const formatTimeLabel = (dayName: string, hour: number): string => {
  const displayHour = String(hour).padStart(2, "0");
  if (hour < 12) {
    return `${dayName}요일 오전 ${displayHour}시`;
  }
  return `${dayName}요일 오후 ${displayHour}시`;
};

// 인덱스에 따른 배지 정렬 방향 결정
const getAlignment = (index: number): "left" | "center" | "right" => {
  // 월(0)은 left, 일(6)은 right, 나머지는 center
  if (index === 0) return "left";
  if (index === 6) return "right";
  return "center";
};

// 정렬 방향에 따른 transform 값
const getTransform = (alignment: "left" | "center" | "right"): string => {
  switch (alignment) {
    case "left":
      return "translateX(0)";
    case "right":
      return "translateX(-100%)";
    default:
      return "translateX(-50%)";
  }
};

const DashboardChartLine: React.FC<DashboardChartLineProps> = ({
  data,
  className = "",
}) => {
  // 호버 상태 관리 (테스트용: 0=월, 1=화, 2=수, 3=목, 4=금, 5=토, 6=일)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // 데이터 변환 및 최대값 찾기
  const { chartData, maxIndex } = useMemo(() => {
    if (!data || data.length === 0) {
      // 기본 데이터
      const defaultData = [
        { dayName: "월", value: 8 },
        { dayName: "화", value: 14 },
        { dayName: "수", value: 17 },
        { dayName: "목", value: 10 },
        { dayName: "금", value: 13 },
        { dayName: "토", value: 1 },
        { dayName: "일", value: 2 },
      ];
      return {
        chartData: defaultData,
        maxIndex: 2,
      };
    }

    let maxIdx = 0;
    let maxVal = -1;

    const transformed = data.map((item, idx) => {
      const val = item.value ?? 0;
      if (val > maxVal) {
        maxVal = val;
        maxIdx = idx;
      }
      return {
        dayName: item.dayName,
        value: val,
      };
    });

    return {
      chartData: transformed,
      maxIndex: maxIdx,
    };
  }, [data]);

  // 활성 인덱스: 호버 중이면 호버 인덱스, 아니면 최대값 인덱스
  // const activeIndex = 6;
  const activeIndex = hoveredIndex ?? maxIndex;
  // 배지 텍스트 생성
  const badgeText = useMemo(() => {
    const item = chartData[activeIndex];
    if (!item) return "";
    return formatTimeLabel(item.dayName, item.value);
  }, [chartData, activeIndex]);

  // 가로선 생성 (0~20, 1단위로 21개 선)
  const gridLines = useMemo(() => {
    const lines = [];
    for (let i = 0; i <= 20; i++) {
      // 4의 배수는 굵은 선, 나머지는 얇은 선
      const isThick = i % 4 === 0;
      lines.push({
        y: i,
        strokeWidth: isThick ? 1 : 0.5,
        opacity: isThick ? 1 : 0.5,
      });
    }
    return lines;
  }, []);

  // CustomDot 컴포넌트
  const CustomDot = (props: any) => {
    const { cx, cy, index } = props;
    const isActive = index === activeIndex;

    return (
      <circle
        key={index}
        cx={cx}
        cy={cy}
        r={isActive ? 5 : 4}
        fill={isActive ? "#FFFFFF" : "#E2D5FC"}
        stroke="#6D2EF2"
        strokeWidth={2}
        style={{
          cursor: "pointer",
          ...(isActive ? { filter: "drop-shadow(0 0 4px #6D2EF2)" } : {}),
        }}
        onMouseEnter={() => setHoveredIndex(index)}
        onMouseLeave={() => setHoveredIndex(null)}
      />
    );
  };

  // 배지 정렬 계산
  const alignment = getAlignment(activeIndex);
  const transform = getTransform(alignment);

  return (
    <div
      className={`relative w-full min-w-[328px] h-[220px] ${className}`}
      data-name="dashboard/chart/line"
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={chartData}
          margin={{ top: 40, right: 10, left: -10, bottom: 0 }}
        >
          <defs>
            {/* 피그마: gradient/main (보라-파랑) with opacity 20% - 수평 그라데이션 */}
            <linearGradient id="areaGradient" x1="0" y1="0.5" x2="1" y2="0.5">
              <stop offset="0%" stopColor="#CD3EC7" stopOpacity={0.2} />
              <stop offset="50%" stopColor="#5328DF" stopOpacity={0.2} />
              <stop offset="100%" stopColor="#2D8EFA" stopOpacity={0.2} />
            </linearGradient>
          </defs>

          {/* 가로선 그리드 - 1단위마다 선, 4단위는 굵게 */}
          {gridLines.map((line) => (
            <ReferenceLine
              key={line.y}
              y={line.y}
              stroke="#EEEEEE"
              strokeWidth={line.strokeWidth}
              strokeOpacity={line.opacity}
            />
          ))}

          <XAxis
            dataKey="dayName"
            axisLine={false}
            tickLine={false}
            tick={{
              fill: "#888888",
              fontSize: 12,
              fontFamily: "Pretendard",
            }}
            dy={10}
          />

          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{
              fill: "#888888",
              fontSize: 12,
              fontFamily: "Pretendard",
            }}
            domain={[0, 20]}
            ticks={[0, 4, 8, 12, 16, 20]}
            width={30}
          />

          <Area
            type="linear"
            dataKey="value"
            stroke="#6D2EF2"
            strokeWidth={2}
            fill="url(#areaGradient)"
            fillOpacity={1}
            dot={CustomDot}
            activeDot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>

      {/* 배지 - 활성 인덱스 점 위로 일정한 거리에 표시 */}
      {badgeText && (
        <div
          className="absolute flex flex-col items-center"
          style={{
            // 차트 margin: left=-10, right=10, YAxis width=30
            // 실제 플롯 시작: 20px (30-10), 플롯 끝: 100% - 10px
            // 플롯 너비: calc(100% - 30px)
            left: `calc(20px + ${
              (activeIndex / (chartData.length - 1)) * (100 - 30 / 3.28)
            }%)`,
            // 차트 영역: top margin(40px)부터 시작, 플롯 높이(약 155px)
            // 점 위치: 40 + (1 - value/20) * 155
            // 배지는 점 위로 36px 오프셋
            top: `${40 + (1 - chartData[activeIndex].value / 20) * 155 - 36}px`,
            transform,
          }}
        >
          <Round size="md" type="primary" style="fill" color="main">
            {badgeText}
          </Round>
        </div>
      )}
    </div>
  );
};

export default DashboardChartLine;
